import type { FormProps } from "antd"
import { Form, message, Space, Spin } from "antd"
import Title from "antd/es/typography/Title"
import { useCallback, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"
import { Paths } from "~/constants"
import { t } from "~/languages/i18n"
import { JobDetailAttributes, JobDetailData } from "~/types/types"
import { fetchJobDetailId, updateJobDetail } from "../service/jobDetail"
import JobDetailForm from "./JobDetailForm"
import Publish from "./Publish"

const EditJobDetail = () => {
  const [jobDetail, setJobDetail] = useState<JobDetailData | null>(null)
  const [status, setStatus] = useState("")
  const [publishDate, setPublishDate] = useState<string | null>(null)

  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm()
  const { id } = useParams<{ id: string }>()

  const navigate = useNavigate()

  const onFinish: FormProps<JobDetailAttributes>["onFinish"] = async values => {
    const dataPayload = {
      ...values,
      publishedAt: status === "Draft" ? null : new Date().toISOString(),
    }

    try {
      await updateJobDetail(Number(id), { data: dataPayload })
      message.success("Job detail updated successfully!")

      form.resetFields()
      navigate(Paths.JOB_DETAIL.LIST.PATH)
    } catch {
      message.error("Failed to update job detail. Please try again.")
    }
  }

  const onFinishFailed: FormProps<JobDetailAttributes>["onFinishFailed"] = () => {}

  const handleFetchJobDetail = useCallback(
    async (id: number) => {
      try {
        setLoading(true)
        const response = await fetchJobDetailId(id)
        setJobDetail(response)
        if (response.attributes.publishedAt) {
          setStatus(t("Published"))
        } else {
          setStatus(t("Draft"))
        }
        form.setFieldsValue(response.attributes)
        setLoading(false)
      } catch {
        message.error("Error fetching job detail")
      }
    },
    [form],
  )

  useEffect(() => {
    if (id) {
      handleFetchJobDetail(Number(id))
    }
  }, [id, handleFetchJobDetail])

  return (
    <>
      {loading ? (
        <div className="flex items-center justify-center min-h-[500px]">
          <Spin size="large" />
        </div>
      ) : (
        <div className="p-4">
          <Space>
            <Title level={2}>{t("Edit Job Detail")}</Title>
          </Space>
          <div className="flex justify-between gap-5">
            <JobDetailForm form={form} onFinish={onFinish} onFinishFailed={onFinishFailed} />
            <div className="w-[22%] pt-2">
              <Publish
                onSubmit={form.submit}
                isCreateEdit={false}
                setStatus={setStatus}
                status={status}
                setPublishDate={setPublishDate}
                setLanguages={() => {}}
                language=""
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default EditJobDetail
